import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { useAuth } from '../lib/auth/AuthContext';
import { useEventState } from '../lib/eventState';
import { getLeaderboard, getMyRank, type LeaderboardEntry } from '../lib/api/leaderboard';
import { trackEvent } from '../lib/plausible';
import LeaderboardTabs from './LeaderboardTabs';

const WEEKS: number[] = [1, 2, 3, 4, 5];
const TOP_N = 10;

const STUDY_HALL_BG = '/assets/images/backgrounds/leaderboard-bg.png';

interface MyRankData {
  hasScore: boolean;
  rank: number | null;
  highScore: number;
  achievedAt: string | null;
}

/**
 * Leaderboard panel — main section of the /leaderboard page.
 *
 * Owns:
 *   - Event state fetch on mount (determines pre-event branch + default tab)
 *   - Week-tab state
 *   - Top-N fetch (refires on tab change, aborts in-flight on change)
 *   - Per-user rank fetch (only when authenticated; aborts on change)
 *
 * Layout:
 *   - Full-bleed study-hall photo background with a 50% black overlay
 *   - "LEADERBOARD_" heading (Orbitron + green `_` underscore)
 *   - Infoback panel: rounded TOP only, vertical purple gradient. The
 *     bottom edge is intentionally flush with no radius so the Play Along
 *     panel below (rounded BOTTOM only) reads as a continuous visual.
 *
 * Pre-event branch: when `phase === 'PRE_EVENT'`, the panel renders a
 * notice telling the visitor when Week 1 airs instead of attempting a
 * leaderboard fetch (there are no scores yet).
 */
export default function LeaderboardSection() {
  const reduce = useReducedMotion();
  const auth = useAuth();

  // Live event state from the shared provider: it schedules a refetch at each
  // week/phase boundary and revalidates on tab refocus, so a visitor parked on
  // this page sees the phase flip (PRE_EVENT → live) and the current-week tab
  // advance WITHOUT a manual refresh — unlike a one-shot mount fetch. The
  // provider also retries a failed fetch on its own, so a transient error
  // self-heals here instead of stranding the panel.
  const { phase, currentWeek, nextTransitionAt, loading: eventLoading } = useEventState();
  const [activeWeek, setActiveWeek] = useState<number | null>(null);
  // The visitor may click a past/future week tab; once they do, stop
  // auto-advancing the tab out from under them when a week rolls.
  const userPickedWeekRef = useRef(false);

  const [entries, setEntries] = useState<LeaderboardEntry[] | null>(null);
  const [myRank, setMyRank] = useState<MyRankData | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  // Default the active tab to the current week, and keep it tracking forward as
  // weeks roll — but only until the visitor manually picks a tab.
  useEffect(() => {
    if (currentWeek == null) return;
    if (userPickedWeekRef.current) return;
    setActiveWeek((prev) => (prev == null || currentWeek > prev ? currentWeek : prev));
  }, [currentWeek]);

  // Per-tab fetch: leaderboard + (if authed) my rank.
  useEffect(() => {
    if (activeWeek == null) return;
    if (phase === 'PRE_EVENT') return; // no scores yet
    if (phase === null) return; // event-state still loading

    const ctl = new AbortController();
    setListLoading(true);
    setListError(null);
    setEntries(null);
    setMyRank(null);

    const tasks: Promise<unknown>[] = [
      getLeaderboard(activeWeek, TOP_N, 0, { signal: ctl.signal }).then((data) => {
        if (ctl.signal.aborted) return;
        setEntries(data.entries);
      }),
    ];

    if (auth.isAuthenticated) {
      tasks.push(
        getMyRank(activeWeek, { signal: ctl.signal })
          .then((data) => {
            if (ctl.signal.aborted) return;
            setMyRank({
              hasScore: data.hasScore,
              rank: data.rank,
              highScore: data.highScore,
              achievedAt: data.achievedAt,
            });
          })
          // Soft-fail: keep showing the public list if the personal fetch breaks
          // (e.g. token went stale and refresh also failed mid-page).
          .catch(() => {
            if (!ctl.signal.aborted) setMyRank(null);
          }),
      );
    }

    Promise.all(tasks)
      .catch((err: unknown) => {
        if ((err as { name?: string }).name === 'AbortError') return;
        setListError('Could not load the leaderboard. Please try again.');
      })
      .finally(() => {
        if (!ctl.signal.aborted) setListLoading(false);
      });

    return () => ctl.abort();
  }, [activeWeek, auth.isAuthenticated, phase]);

  function handleTabChange(week: number): void {
    userPickedWeekRef.current = true;
    setActiveWeek(week);
    trackEvent('Leaderboard Tab Click', { week: String(week) });
  }

  return (
    <motion.section
      aria-labelledby="leaderboard-heading"
      className="relative isolate overflow-hidden px-4 pt-20 pb-16 sm:px-12 sm:pt-24 sm:pb-20 lg:px-[50px] lg:pt-[125px] lg:pb-[80px]"
      initial={reduce ? false : { opacity: 0, y: 24 }}
      whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={reduce ? { duration: 0 } : { duration: 0.4, ease: 'easeOut' }}
    >
      {/* Blurred full-bleed fill: same image scaled up so its soft edges
          push off-screen, filling the side gutters on screens wider than the
          sharp layer's cap. Painted under the sharp copy (later DOM, same z). */}
      <img
        src={STUDY_HALL_BG}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-20 h-full w-full scale-110 select-none object-cover blur-2xl"
      />
      {/* Sharp copy: capped at 1600px and centered so it stops expanding (and
          over-upscaling) on HD/UHD. ≤1600px viewports render identically to a
          single full-bleed image. */}
      <img
        src={STUDY_HALL_BG}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-20 mx-auto h-full w-full max-w-[1600px] select-none object-cover"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 bg-black/50"
      />

      <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-6 sm:gap-[25px]">
        <h1
          id="leaderboard-heading"
          className="font-[Orbitron] uppercase text-white text-[clamp(28px,7vw,40px)] md:text-[clamp(40px,4.5vw,48px)] tracking-[-0.03em] leading-[0.95]"
        >
          Leaderboard
          <span className="text-[#00ff8b]">_</span>
        </h1>

        <div
          // minHeight reserves the populated-panel height up front so the
          // page doesn't pop big→small as fetches resolve. Sized to fit
          // the tabs row + 10 list rows + bottom padding with a small
          // buffer for the your-rank row when it lands.
          className="relative min-h-[clamp(440px,55vh,560px)] overflow-hidden rounded-tl-[15px] rounded-tr-[15px] px-4 pt-8 pb-12 sm:px-12 sm:pt-10 sm:pb-14 lg:px-[100px] lg:pt-[40px] lg:pb-[60px]"
          style={{
            // Three-layer stack matching the CompetitorsSection inset pattern:
            //   1. Purple gradient on top (alpha-translucent so the stripes peek through)
            //   2. diagonal.svg pattern tile at the base
            // CSS paints background-image in declared order top→bottom.
            backgroundImage: [
              'linear-gradient(to bottom, rgba(65, 56, 80, 0.85) 0%, rgba(140, 5, 234, 0.85) 100%)',
              "url('/assets/patterns/diagonal.svg')",
            ].join(', '),
            backgroundRepeat: 'no-repeat, repeat',
          }}
        >
          {phase === null ? (
            eventLoading ? (
              <PanelMessage tone="muted">Loading…</PanelMessage>
            ) : (
              <PanelMessage tone="error">
                Could not load the event state. Retrying…
              </PanelMessage>
            )
          ) : phase === 'PRE_EVENT' ? (
            <PreEventNotice nextTransitionAt={nextTransitionAt} />
          ) : (
            <>
              <LeaderboardTabs
                weeks={WEEKS}
                activeWeek={activeWeek ?? WEEKS[0]!}
                onChange={handleTabChange}
              />

              <div className="mx-auto mt-8 max-w-[540px] sm:mt-10" id="leaderboard-list">
                {listLoading && entries === null ? (
                  <LeaderboardSkeleton />
                ) : listError ? (
                  <PanelMessage tone="error">{listError}</PanelMessage>
                ) : entries && entries.length === 0 ? (
                  <PanelMessage tone="muted">
                    No scores recorded for this week yet — check back soon.
                  </PanelMessage>
                ) : entries ? (
                  <>
                    <LeaderboardList entries={entries} />
                    {auth.isAuthenticated && myRank?.hasScore && myRank.rank != null && myRank.rank > TOP_N && (
                      <YourRankRow
                        rank={myRank.rank}
                        displayName={auth.user?.displayName || auth.user?.email || 'You'}
                        score={myRank.highScore}
                      />
                    )}
                  </>
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>
    </motion.section>
  );
}

function LeaderboardList({ entries }: { entries: LeaderboardEntry[] }) {
  return (
    <ul className="flex flex-col gap-[10px] text-white uppercase">
      {entries.map((entry) => (
        <LeaderboardRow
          key={entry.userId}
          rank={entry.rank}
          name={entry.displayName}
          score={entry.highScore}
        />
      ))}
    </ul>
  );
}

function LeaderboardRow({ rank, name, score }: { rank: number; name: string; score: number }) {
  return (
    <li className="grid grid-cols-[2.5rem_1fr_auto] items-baseline gap-3 text-[clamp(13px,1.5vw,16px)] sm:gap-6">
      <span className="font-[Inter] font-light tabular-nums">{rank}</span>
      <span className="truncate font-[Inter] font-bold">{name}</span>
      <span className="text-right font-[Inter] font-bold tabular-nums">{formatScore(score)}</span>
    </li>
  );
}

function YourRankRow({ rank, displayName, score }: { rank: number; displayName: string; score: number }) {
  return (
    <div
      className="mt-6 border-t border-white/15 pt-4"
      aria-label="Your rank"
    >
      <div className="grid grid-cols-[2.5rem_1fr_auto] items-baseline gap-3 text-white uppercase text-[clamp(13px,1.5vw,16px)] sm:gap-6">
        <span className="font-[Inter] font-bold tabular-nums">{rank}</span>
        <span className="truncate font-[Inter] font-bold">{displayName}</span>
        <span className="text-right font-[Inter] font-bold tabular-nums">{formatScore(score)}</span>
      </div>
    </div>
  );
}

function LeaderboardSkeleton() {
  return (
    <ul className="flex flex-col gap-[10px]" aria-busy="true" aria-label="Loading leaderboard">
      {Array.from({ length: TOP_N }, (_, i) => (
        <li
          key={i}
          className="grid grid-cols-[2.5rem_1fr_auto] items-center gap-3 text-[clamp(13px,1.5vw,16px)] sm:gap-6"
        >
          <span className="h-3 w-6 rounded bg-white/15" />
          <span className="h-3 w-[60%] rounded bg-white/15" />
          <span className="h-3 w-16 rounded bg-white/15" />
        </li>
      ))}
    </ul>
  );
}

function PanelMessage({
  tone,
  children,
}: {
  tone: 'muted' | 'error';
  children: ReactNode;
}) {
  const cls =
    tone === 'error'
      ? 'text-white/90 bg-red-900/40 border border-red-300/30'
      : 'text-white/80 bg-white/5 border border-white/10';
  return (
    <p className={`rounded-lg px-4 py-3 text-center font-[Inter] text-sm ${cls}`}>{children}</p>
  );
}

function PreEventNotice({ nextTransitionAt }: { nextTransitionAt: string | null }) {
  const transitionDate = nextTransitionAt ? new Date(nextTransitionAt) : null;
  const formatted = transitionDate
    ? transitionDate.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        timeZone: 'America/New_York',
      })
    : null;
  return (
    <div className="flex flex-col items-center gap-3 py-10 text-center font-[Inter] text-white">
      <h2 className="font-[Orbitron] uppercase text-[clamp(18px,3vw,24px)] tracking-[0.08em]">
        Leaderboards open soon
      </h2>
      <p className="max-w-[480px] text-white/85 text-[clamp(14px,1.8vw,16px)]">
        Jake&apos;s Neighborhood Swarm goes live{' '}
        {formatted ? <strong className="font-bold">{formatted}</strong> : 'soon'} at{' '}
        <strong className="font-bold">6&nbsp;PM&nbsp;ET</strong>. Come back then to see the
        Week&nbsp;1 standings.
      </p>
    </div>
  );
}

function formatScore(n: number): string {
  // Matches the Figma example "10.125 / 14.500" formatting — period as the
  // thousands separator. Game scores are integers; render with en-US then
  // swap the comma for a period to match the design.
  return n.toLocaleString('en-US').replace(/,/g, '.');
}
